import { FC, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'
import {
  AtendimentoType,
  ButtonType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { tiposAtendimentoSchema } from './validation/tiposAtendimentoSchema'
import {
  bindFormularioToEmpresasWithTipos,
  bindFormularioToUnidadesWithTipos,
} from '../../core/_requests'
import {
  Button,
  FormRadio,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  FormularioDtoTipoAtendimentoUnidadeCreate,
  FormularioDtoTipoAtendimentoCreate,
} from '../../core/_models'
import { VinculoMode } from '../../../vinculos-especialidade-list/components/modal/validation/epecialidadeVinculoSchema'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'

type IProps = {
  formularioId: number
}
export const TiposAtendimentoVinculoModal: FC<IProps> = ({ formularioId }) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const initialFormulario: {
    tipoAtendimento: IOption<AtendimentoType> | undefined
    empresas: IOption<number>[]
    empresa: IOption<number> | undefined
    unidades: IOption<number>[]
    mode: VinculoMode | undefined
  } = {
    tipoAtendimento: undefined,
    empresas: [],
    empresa: undefined,
    unidades: [],
    mode: undefined,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormulario,
    validationSchema: useMemo(() => tiposAtendimentoSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      try {
        if (formik.values.mode === VinculoMode.Empresa) {
          const data: FormularioDtoTipoAtendimentoCreate = {
            formulario: Number(formularioId),
            tipoAtendimento: values.tipoAtendimento?.value as AtendimentoType,
            empresas: values.empresas.map((empresa) => empresa.value),
          }
          await bindFormularioToEmpresasWithTipos(data)
          showAlert({ title: 'Sucesso', text: 'Vínculo criado com sucesso!', icon: 'success' })
        } else if (formik.values.mode === VinculoMode.Unidade) {
          const data: FormularioDtoTipoAtendimentoUnidadeCreate = {
            formulario: Number(formularioId),
            tipoAtendimento: values.tipoAtendimento?.value as AtendimentoType,
            unidades: values.unidades.map((unidade) => unidade.value),
          }
          await bindFormularioToUnidadesWithTipos(Number(formik.values.empresa?.value), data)
          showAlert({ title: 'Sucesso', text: 'Vínculo criado com sucesso!', icon: 'success' })
        }

        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik

  useEffect(() => {
    if (!formik.values.empresa) setFieldValue('unidades', [])
  }, [formik.values.empresa, setFieldValue])

  const empresaParams = useMemo(
    () => ({ empresaId: Number(formik.values.empresa?.value) }),
    [formik.values.empresa?.value]
  )

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        label='Tipo de Atendimento'
        name='tipoAtendimento'
        fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
        placeholder='Selecione um tipo de atendimento'
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isRequired
      />
      <FormRadio
        formik={formik}
        label='Vincular por'
        name='mode'
        options={[
          { id: 1, descricao: 'Instituição', value: VinculoMode.Empresa },
          { id: 2, descricao: 'Unidade', value: VinculoMode.Unidade },
        ]}
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isRequired
      />
      {formik.values.mode && (
        <>
          <FormSelect
            key={formik.values.mode}
            formik={formik}
            entity='empresa'
            name={formik.values.mode === VinculoMode.Unidade ? 'empresa' : 'empresas'}
            fetchOptions={OptionsHelper.getOptionsEmpresas}
            isDisabled={formik.isSubmitting}
            isMulti={formik.values.mode !== VinculoMode.Unidade}
            className='mb-3'
            isRequired
          />
          {formik.values.mode === VinculoMode.Unidade && (
            <FormSelect
              formik={formik}
              name='unidades'
              label='Unidades/Setores'
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={empresaParams}
              placeholder='Selecione pelo menos uma unidade'
              isDisabled={formik.isSubmitting}
              className='mb-3'
              isRequired
              isMulti
            />
          )}
        </>
      )}

      <div className='d-flex justify-content-center pt-15'>
        <Button onClick={closeModal} className='btn-secondary me-3' disabled={formik.isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
